// Single non-SSE HTTP client for the application (per the amended constitution:
// every fetch outside the SSE stream in App.tsx goes through this module).
import type { AuthUser, QuotaSnapshot, RunDetail, RunListResponse } from "../types";

export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const HTTP_STATUS_NO_CONTENT = 204;
export const HTTP_STATUS_UNAUTHORIZED = 401;
export const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
export const HTTP_STATUS_NOT_FOUND = 404;
export const HTTP_STATUS_CONFLICT = 409;

const DEFAULT_ERROR_MESSAGE = "Falha na comunicação com o servidor.";

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export class QuotaExceededError extends ApiError {
  quota: QuotaSnapshot;

  constructor(status: number, body: unknown, message: string, quota: QuotaSnapshot) {
    super(status, body, message);
    this.name = "QuotaExceededError";
    this.quota = quota;
  }
}

function extractErrorMessage(body: unknown): string | undefined {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (typeof record.detail === "string") return record.detail;
    if (typeof record.error === "string") return record.error;
  }
  return undefined;
}

function hasQuota(body: unknown): body is { quota: QuotaSnapshot } {
  return typeof body === "object" && body !== null && "quota" in body;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });

  const body: unknown =
    response.status === HTTP_STATUS_NO_CONTENT
      ? undefined
      : await response.json().catch(() => undefined);

  if (!response.ok) {
    const message = extractErrorMessage(body) || DEFAULT_ERROR_MESSAGE;
    if (response.status === HTTP_STATUS_TOO_MANY_REQUESTS && hasQuota(body)) {
      throw new QuotaExceededError(response.status, body, message, body.quota);
    }
    throw new ApiError(response.status, body, message);
  }

  return body as T;
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const body = await apiFetch<{ user: AuthUser }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  return body.user;
}

export async function logout(): Promise<void> {
  await apiFetch<void>("/api/auth/logout", { method: "POST" });
}

export async function fetchMe(): Promise<AuthUser> {
  const body = await apiFetch<{ user: AuthUser }>("/api/auth/me");
  return body.user;
}

export async function fetchQuota(): Promise<QuotaSnapshot> {
  const body = await apiFetch<{ quota: QuotaSnapshot }>("/api/underwrite/quota");
  return body.quota;
}

export async function fetchRuns(
  cursor?: string | null,
  limit?: number
): Promise<RunListResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (limit !== undefined) params.set("limit", String(limit));
  const query = params.toString();
  return apiFetch<RunListResponse>(`/api/underwrite/runs${query ? `?${query}` : ""}`);
}

export async function fetchRun(id: string): Promise<RunDetail> {
  return apiFetch<RunDetail>(`/api/underwrite/runs/${encodeURIComponent(id)}`);
}

export async function deleteRun(id: string): Promise<void> {
  await apiFetch<void>(`/api/underwrite/runs/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
